// DashboardsAutogenPage — standalone home for the "Autogenerate from gwbk"
// flow at `/dashboards/autogenerate`. Unlike the old modal, runs are listed
// from the server (and persisted on the api-service), so you can leave this
// page, come back, and still watch in-flight runs or review finished ones.
//
// Layout mirrors the rest of the explorer: a left list of past/active runs
// (`GwbkAutogenRunList`) + a main detail pane (the shared `GwbkAutogenJobView`).
// The selected run lives on the URL (`?job=<id>`) so deep links and browser
// back behave. "New run" opens the setup dialog; on start the job is selected.

import { useEffect, useMemo, useState } from 'react'
import { ArrowLeft, Download, Loader2, Plus, Trash2, X } from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  gwbkAutogenDownloadUrl,
  useCancelGwbkAutogenMutation,
  useDeleteGwbkAutogenMutation,
  useGetGwbkAutogenJobQuery,
  useListGwbkAutogenJobsQuery
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../components/ui/button'
import GwbkAutogenDialog from './GwbkAutogenDialog'
import GwbkAutogenJobView from './GwbkAutogenJobView'
import GwbkAutogenRunList from './GwbkAutogenRunList'
import { isActiveState } from './gwbk-autogen-status'

const SEARCH_JOB = 'job'

const DashboardsAutogenPage = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedId = searchParams.get(SEARCH_JOB) ?? ''

  const [dialogOpen, setDialogOpen] = useState(false)

  const listQuery = useListGwbkAutogenJobsQuery()
  const jobs = useMemo(() => listQuery.data ?? [], [listQuery.data])

  const jobQuery = useGetGwbkAutogenJobQuery(selectedId, {
    skip: selectedId === ''
  })
  const job = jobQuery.data

  const [cancelAutogen, cancelState] = useCancelGwbkAutogenMutation()
  const [deleteAutogen, deleteState] = useDeleteGwbkAutogenMutation()

  // Default the selection to the newest run once the list loads.
  useEffect(() => {
    const newest = jobs[0]
    if (selectedId === '' && newest) {
      const next = new URLSearchParams(searchParams)
      next.set(SEARCH_JOB, newest.id)
      setSearchParams(next, { replace: true })
    }
  }, [selectedId, jobs, searchParams, setSearchParams])

  const selectJob = (id: string) => {
    const next = new URLSearchParams(searchParams)
    next.set(SEARCH_JOB, id)
    setSearchParams(next, { replace: true })
  }

  const handleStarted = (jobId: string) => {
    setDialogOpen(false)
    selectJob(jobId)
    void listQuery.refetch()
  }

  const handleCancel = async () => {
    if (!job) return
    try {
      await cancelAutogen(job.id).unwrap()
    } catch {
      // Best-effort; the poll reflects the real state.
    }
  }

  const handleDelete = async () => {
    if (!job) return
    try {
      await deleteAutogen(job.id).unwrap()
      // Drop the selection so we don't render a dangling pointer.
      const next = new URLSearchParams(searchParams)
      next.delete(SEARCH_JOB)
      setSearchParams(next, { replace: true })
    } catch {
      // Surface nothing for now; the list will simply keep the row.
    }
  }

  const jobActive = isActiveState(job?.state)

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-3 border-b border-line px-4 py-2.5">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/dashboards')}
          aria-label="Back to dashboards"
          title="Back to dashboards"
          className="size-7"
        >
          <ArrowLeft className="size-4" aria-hidden />
        </Button>
        <div className="flex flex-col">
          <h1 className="m-0 text-sm font-semibold text-ink-1">Autogenerate from gwbk</h1>
          <p className="m-0 text-xs text-ink-3">
            Capture an Ignition gateway's views and build dashboards from them.
          </p>
        </div>
        <Button className="ml-auto" onClick={() => setDialogOpen(true)}>
          <Plus className="size-4" aria-hidden />
          New run
        </Button>
      </div>

      <div className="flex min-h-0 flex-1">
        <aside className="flex w-72 shrink-0 flex-col overflow-auto border-r border-line">
          <GwbkAutogenRunList
            jobs={jobs}
            selectedId={selectedId}
            isLoading={listQuery.isLoading}
            onSelect={selectJob}
            onNewRun={() => setDialogOpen(true)}
          />
        </aside>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {selectedId === '' ? (
            <div className="flex flex-1 items-center justify-center p-12 text-sm text-ink-3">
              Select a run to view its progress and results.
            </div>
          ) : jobQuery.isLoading && !job ? (
            <div className="flex flex-1 items-center gap-2 p-6 text-sm text-ink-3">
              <Loader2 className="size-4 animate-spin" aria-hidden />
              Loading run…
            </div>
          ) : !job ? (
            <div className="flex flex-1 items-center justify-center p-12 text-sm text-ink-3">
              This run could not be loaded. It may have been deleted.
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 border-b border-line px-4 py-2">
                <div className="flex min-w-0 flex-col">
                  <span className="truncate text-sm font-medium text-ink-1" title={job.gatewayUrl}>
                    {job.gatewayId || job.gatewayUrl}
                  </span>
                  <span className="truncate font-mono text-10 text-ink-3">{job.id}</span>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  {jobActive ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => void handleCancel()}
                      disabled={cancelState.isLoading}
                    >
                      <X className="size-4" aria-hidden />
                      Stop
                    </Button>
                  ) : null}
                  {job.shots.length > 0 ? (
                    <Button size="sm" asChild>
                      <a href={gwbkAutogenDownloadUrl(job.id)}>
                        <Download className="size-4" aria-hidden />
                        Download all
                      </a>
                    </Button>
                  ) : null}
                  {!jobActive ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => void handleDelete()}
                      disabled={deleteState.isLoading}
                      aria-label="Delete run"
                      title="Delete run"
                    >
                      <Trash2 className="size-4" aria-hidden />
                    </Button>
                  ) : null}
                </div>
              </div>
              <div className="flex min-h-0 flex-1 flex-col overflow-auto px-4 py-2">
                <GwbkAutogenJobView job={job} />
              </div>
            </>
          )}
        </div>
      </div>

      <GwbkAutogenDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onStarted={handleStarted}
      />
    </div>
  )
}

export default DashboardsAutogenPage
